import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActionsService } from '../../api/actions.service';
import { Router } from '@angular/router';
import { StorageService } from '../../services/storage.service';
import { FORM_MASK } from '../../constants/form-masks.const';
import { VALIDATION_ERRORS_MESSAGE } from '../../constants/validation-errors.const';
import { scan, takeWhile } from 'rxjs';
import { RegistrationStep } from '../../funnel/registration-step';
import Routes from '../../constants/routes.const';
import removeExtraCharacters from '../../utils/remove-extra-characters';
import { Title } from '@angular/platform-browser';
import { ClientActionService } from '../../services/client-action.service';
import { ValidationService } from '../../components/form-fields/validation.service';
import timer from '../../utils/timer';
import detectMobile from 'src/app/utils/detect-mobile';
import { AgreementASP } from 'src/app/constants/other-links.const';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { DuplicationService } from '../../services/duplication.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular-ivy';
import {AnalyticsEventsService} from "../../services/analytic.service";

declare var actionCallback: any;

@Component({
  selector: 'app-confirmation-sms',
  templateUrl: './confirmation-sms.component.html',
  styleUrls: ['./confirmation-sms.component.sass'],
  providers: [StorageService, ClientActionService],
})
export class ConfirmationSmsComponent
  extends RegistrationStep
  implements OnInit, OnDestroy
{
  public brand: string;
  private formId: number;
  private phoneNumber: number;

  public duplicatesStatus: number;
  public loading = false;

  public isValidCode = true;
  public confirmationForm: UntypedFormGroup;

  public timesLeft: number;
  private timerId;
  private _timerSub: any;
  public ERRORS_MESSAGE;
  public MASK;

  public isMobile = detectMobile();
  public agreementLink: string;

  private isFunctionCalled: boolean = false;

  constructor(
    fb: UntypedFormBuilder,
    private actionService: ActionsService,
    private router: Router,
    private _storageService: StorageService,
    private clientActionService: ClientActionService,
    private titleService: Title,
    private recaptchaV3Service: ReCaptchaV3Service,
    private duplicationService: DuplicationService,
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    public _analyticService: AnalyticsEventsService
  ) {
    super(RegistrationStep.CONFIRMATION_STEP);

    this.confirmationForm = fb.group({
      confirmationCode: [
        '',
        Validators.compose([
          Validators.required,
          ValidationService.checkSmsConfirmationCode,
        ]),
      ],
    });
    this.ERRORS_MESSAGE = VALIDATION_ERRORS_MESSAGE;
    this.MASK = FORM_MASK;
    this.agreementLink = AgreementASP[this.brand];
  }

  ngOnInit() {
    this.brand = this.config.data.brand;
    this.formId = this.config.data.formId;
    this.phoneNumber = this.config.data.phoneNumber;

    this._analyticService.eventYandexMetrica('open_popup_confirmation_sms')

    this.initTimer();
  }

  initTimer() {
    if (this.phoneNumber && this._storageService.get('TIMEOUTS')) {
      const timeouts = JSON.parse(this._storageService.get('TIMEOUTS'));
      const msStart = timeouts[this.phoneNumber].start;
      const msPassed = Date.now() - msStart;
      this.timesLeft = Math.floor(
        (timeouts[this.phoneNumber].msLeft - msPassed) / 1000
      );
      if (this.timesLeft <= 0) {
        this.timesLeft = 0;
        return null;
      }
      const timeLeft = this.timesLeft;
      this._timerSub = timer(0, 1000)
        .pipe(
          scan((acc) => --acc, timeLeft),
          takeWhile((x) => x >= 0)
        )
        .subscribe((res) => {
          this.timesLeft = res;
          if (res <= 0) {
            this.timesLeft = 0;
            this._timerSub.unsubscribe();
          }
        });
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.timerId);
    if (this._timerSub) {
      this._timerSub.unsubscribe();
    }
  }

  // hideComponent(increased) {
  //   increased = false;
  //   this.changed.emit(increased);
  // }

  getConfirmationCode() {
    this.config.data.resendConfirmationCode.next(true);
  }

  pad(num: number) {
    return ('0' + num).slice(-2);
  }

  convertSecondsToMinutes(secs: number) {
    const minutes = Math.floor(secs / 60);
    secs = secs % 60;
    return this.pad(minutes) + ':' + this.pad(secs);
  }

  public sendSmsCode(): void {
    if (!this.isFunctionCalled) {
      this.sendWithCaptcha()
      this.isFunctionCalled = true
    }
  }

  public sendWithCaptcha(): void {
    let tokenStr;
    this.recaptchaV3Service
      .execute('sendCodeLogin')
      .pipe(timeout(30000))
      .subscribe({
        next: (token: string) => {
          tokenStr = token;
        },
        error: (err: string) => {
          this.submitConfirmForm(tokenStr);
        },
        complete: () => {
          this.submitConfirmForm(tokenStr);
        },
      });
  }

  // отправка кода подтверждения
  submitConfirmForm(tokenStr) {
    const form = {
      confirmationCode: removeExtraCharacters(
        this.confirmationForm.get('confirmationCode').value
      ),
      id: this.formId,
      currentStage: this.stepNumber,
    };
    if (form.confirmationCode.length === 5) {
      this.loading = true;
      this.actionService
        .sendRegistrationForm(
          {
            source: 'SITE',
            form: form,
          },
          this.stepNumber,
          tokenStr
        )
        .subscribe({
          next: (res) => {
            if (res.status === 'SUCCESS') {
              const confirmation = this.duplicationService.checkDuplications(
                res.data?.handlers
              );

              if (!confirmation) {
                this.ref.destroy();
                return null;
              }

              this.ref.destroy();
              this.router.navigate([Routes.SECOND_REG_STEP], {
                queryParamsHandling: 'merge',
              });
              actionCallback(
                RegistrationStep.CONFIRMATION_STEP,
                'confirmation'
              );
              this._analyticService.eventYandexMetrica('success_sms_code_registration')
            } else {
              this.isValidCode = false;
              this.loading = false;
              this.confirmationForm
                .get('confirmationCode')
                .setErrors({ confirmationCodeNotMatch: true });
              this.confirmationForm.get('confirmationCode').markAsTouched();
              this._analyticService.eventYandexMetrica('not_valid_verification_code_reg')
              this.isFunctionCalled = false
            }
          },
          error: (error) => {
            error.form = form;
            error.phone = this.phoneNumber;
            Sentry.captureException(error);
            this.isValidCode = false;
            this.loading = false;
            this.confirmationForm
              .get('confirmationCode')
              .setErrors({ confirmationCodeNotMatch: true });
            this.confirmationForm.get('confirmationCode').markAsTouched();
            this.isFunctionCalled = false
          },
        });
    } else {
      this._analyticService.eventYandexMetrica('not_valid_verification_code_reg')
    }
  }

  public checkConfirmForm(): void {
    const value = removeExtraCharacters(
      this.confirmationForm.get('confirmationCode').value
    );
    if (value.length !== 5 && value.length > 0) {
      this.isValidCode = false;
      this.confirmationForm
        .get('confirmationCode')
        .setErrors({ smsConfirmationLength: true });
      this.confirmationForm.get('confirmationCode').markAsTouched();
    }
  }

  returnToUpdateForm() {
    this.ref.close();
    this._analyticService.eventYandexMetrica('verification_code_registration_interrupted')
  }
}
